import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';

@Directive({
  selector: 'textarea[appMaxRows]'
})
export class MaxRowsDirective implements OnInit {
  @Input('appMaxRows') maxRows = 3;

  constructor(private el: ElementRef<HTMLTextAreaElement>) {}

  ngOnInit(): void {
    const ta = this.el.nativeElement;
    ta.style.overflow = 'hidden';   
    ta.style.resize = 'none';
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const allowedControlKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Escape'
    ];
    if (event.ctrlKey || event.metaKey || allowedControlKeys.includes(event.key)) {
      return;
    }
    // ignore modifier-only presses (Shift, CapsLock, F-keys, etc.)
    if (event.key.length > 1 && event.key !== 'Enter') {
      return;
    }

    const textarea = this.el.nativeElement;
    const insertChar = event.key === 'Enter' ? '\n' : event.key;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const newValue = textarea.value.slice(0, start) + insertChar + textarea.value.slice(end);

    if (this.willExceedMaxRows(textarea, newValue)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const textarea = this.el.nativeElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const fullValue = textarea.value.slice(0, start) + pasted + textarea.value.slice(end);

    if (this.willExceedMaxRows(textarea, fullValue)) {
      event.preventDefault();
      const fitting = this.getFittingText(textarea, start, end, pasted);
      if (fitting) {
        const finalValue = textarea.value.slice(0, start) + fitting + textarea.value.slice(end);
        textarea.value = finalValue;
        const caret = start + fitting.length;
        textarea.setSelectionRange(caret, caret);
        textarea.dispatchEvent(new Event('input', { bubbles: true })); // sync Angular FormControl
      }
    }
  }

  private willExceedMaxRows(textarea: HTMLTextAreaElement, value: string): boolean {
    const originalValue = textarea.value;
    const originalHeight = textarea.style.height;

    textarea.value = value;
    textarea.style.height = 'auto';

    const computed = getComputedStyle(textarea);
    let lineHeight = parseFloat(computed.lineHeight);
    if (isNaN(lineHeight)) lineHeight = parseFloat(computed.fontSize) * 1.2;

    const maxHeight = lineHeight * this.maxRows;
    const exceeds = textarea.scrollHeight > maxHeight + 1; // +1px rounding tolerance

    textarea.value = originalValue;
    textarea.style.height = originalHeight;
    return exceeds;
  }

  // binary search the largest prefix of pasted text that still fits
  private getFittingText(textarea: HTMLTextAreaElement, start: number, end: number, pasted: string): string {
    let low = 0, high = pasted.length, fit = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = pasted.slice(0, mid);
      const newValue = textarea.value.slice(0, start) + candidate + textarea.value.slice(end);
      if (!this.willExceedMaxRows(textarea, newValue)) {
        fit = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return pasted.slice(0, fit);
  }
}
